import { useEffect, useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import type {
  ApiClient, Modellupload, Planblatt, Planmodellzeile, Planrevision,
} from '../api';
import type { Rahmen } from './Projektrahmen';
import { IconOk, IconTabelle, IconHochladen } from '../Icons';
import { useModellSchreibrecht } from '../rechte';
import { PageHeader } from '../rahmen/PageHeader';
import {
  ConfirmDialog, DataTable, EmptyState, ErrorState, Feld, FormSection,
  KpiCard, KpiReihe, LoadingState, StatusBadge, type Spalte,
} from '../muster';

/**
 * Plannummern, Indexstände und die Modelluploadliste.
 *
 * Die App ist die führende Quelle. Beim ersten Auslesen entsteht der Bestand aus dem
 * Modell; danach wird hier gepflegt und in die Modelle übertragen.
 *
 * Wo das Modell abweicht, steht ein Konflikt — er wird nicht stillschweigend
 * überschrieben, sondern muss freigegeben werden. Wer im Modell etwas geändert hat,
 * hatte vielleicht einen Grund.
 */
export function Planstaende() {
  const { api, projekt } = useOutletContext<Rahmen>();

  const [zeilen, setZeilen] = useState<Planmodellzeile[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [offen, setOffen] = useState<string | null>(null);

  /**
   * Die Profilfassung je Revit-Version.
   *
   * Ein Auftrag braucht sie, und sie hängt an der Version des Modells — bei
   * mehreren Versionen im Projekt sind es mehrere. Deshalb je Version einmal
   * geholt und nicht je Modell.
   */
  const [profile, setProfile] = useState<Record<number, string>>({});

  useEffect(() => {
    const versionen = [...new Set(
      projekt.modelle.filter((m) => m.isActive).map((m) => m.revitVersion))];

    Promise.all(versionen.map(async (v) => {
      try {
        const p = await api.profile(projekt.id, v);
        const id = p.flatMap((x) => x.versions)[0]?.id;
        return id ? ([v, id] as const) : null;
      } catch {
        return null;
      }
    })).then((paare) => {
      const gefunden: Record<number, string> = {};
      for (const x of paare) if (x) gefunden[x[0]] = x[1];
      setProfile(gefunden);
    });
  }, [api, projekt.id, projekt.modelle]);

  const laden = async () => {
    try {
      setZeilen(await api.planuebersicht(projekt.id));
      setFehler(null);
    } catch (e) {
      setFehler(e);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { laden(); }, [api, projekt.id]);

  const kopf = (
    <PageHeader
      titel="Planstände"
      beschreibung={'Die Plattform ist die führende Quelle. Beim ersten Auslesen '
        + 'entsteht der Bestand aus dem Modell; danach wird hier gepflegt und in die '
        + 'Modelle übertragen. Wo das Modell abweicht, steht ein Konflikt — er wird '
        + 'nicht stillschweigend überschrieben, sondern muss freigegeben werden.'}
      meta={zeilen ? `${zeilen.length} Modelle` : undefined}
    />
  );

  if (fehler && !zeilen) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Planstände konnten nicht geladen werden."
          technisch={fehler}
          beiErneutVersuchen={laden}
        />
      </>
    );
  }

  if (!zeilen) {
    return (
      <>
        {kopf}
        <LoadingState text="Planstände werden geladen …" zeilen={6} spalten={4} />
      </>
    );
  }

  const konflikte = zeilen.reduce(
    (n, z) => n + z.plaeneKonflikt + z.revisionenKonflikt, 0);
  const ohneSchluessel = zeilen.filter((z) => !z.modellschluessel).length;

  // Verbunden heißt: Der Schlüssel ist vergeben UND steht im Modell. Nur das ist
  // die Voraussetzung dafür, dass ein Add-in das Modell wiedererkennt.
  const verbunden = zeilen.filter((z) => z.modellschluessel && z.schluesselAm).length;
  const offeneUebertragung = zeilen.reduce((n, z) => n + z.offeneUebertragung, 0);

  // Entwürfe zählen getrennt: Sie sind nicht "offen zu übertragen", sondern noch in
  // Arbeit — der Unterschied entscheidet, ob man den Knopf drücken soll.
  const entwuerfe = zeilen.reduce((n, z) => n + z.entwuerfe, 0);

  return (
    <>
      {kopf}

      <KpiReihe>
        <KpiCard
          titel="Pläne in Listen"
          wert={zeilen.reduce((n, z) => n + z.inListe, 0)}
          vergleich={`von ${zeilen.reduce((n, z) => n + z.plaene, 0)} Plänen`}
        />
        <KpiCard
          titel="Abweichungen" wert={konflikte}
          zustand={konflikte > 0 ? 'warnung' : undefined}
          zustandText={konflikte > 0 ? 'im Modell anders' : undefined}
        />
        <KpiCard
          titel="Nicht übertragen" wert={offeneUebertragung}
          // Entwürfe zählen getrennt: Sie sind nicht „offen zu übertragen",
          // sondern noch in Arbeit — der Unterschied entscheidet, ob man den
          // Knopf drücken soll.
          vergleich={entwuerfe > 0 ? `${entwuerfe} noch Entwurf` : undefined}
        />
        <KpiCard
          titel="Verbunden" wert={verbunden}
          vergleich={`von ${zeilen.length} Modellen`}
          // Ohne Modell ist nichts „erkennbar": 0 === 0 wäre wahr und die
          // Karte trüge eine grüne Marke über einem leeren Bestand.
          zustand={ohneSchluessel > 0 ? 'warnung'
            : zeilen.length > 0 && verbunden === zeilen.length ? 'gut' : undefined}
          zustandText={ohneSchluessel > 0 ? `${ohneSchluessel} ohne Schlüssel`
            : zeilen.length > 0 && verbunden === zeilen.length
              ? 'alle Modelle erkennbar' : undefined}
        />
      </KpiReihe>

      <p className="schwach" style={{ fontSize: 12 }}>
        Was in den Modellen dafür stimmen muss, steht unter{' '}
        <Link to="/modellvorgaben">Modellvorgaben</Link> — Modellschlüssel,
        Startansicht, Nummerierung der Revisionen, Titelblöcke.
      </p>

      {/* Der Modellschlüssel ist die Voraussetzung für alles Weitere: Ohne ihn
          kann ein Add-in nicht wissen, in welchem Modell es steckt. */}
      {ohneSchluessel > 0 && (
        <div className="hinweis">
          <div>
            <strong>{ohneSchluessel} Modell(e) ohne Schlüssel.</strong>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Er wird hier vergeben und beim nächsten Übertragen in die
              Projektinformation geschrieben — dafür muss der gemeinsam genutzte
              Parameter <code>TuV_Modellschluessel</code> im Modell angelegt sein.
              Ein Automatiklauf legt keine Parameter an.
            </div>
          </div>
        </div>
      )}

      {zeilen.length === 0 && (
        <EmptyState
          icon={<IconTabelle />}
          titel="Noch kein Modell mit Planständen."
          erklaerung={'Der Bestand entsteht aus einem Auftrag „Pläne lesen" — er liest '
            + 'Plannummern, Indexstände und Modellkennzahlen aus dem Modell.'}
        />
      )}

      {zeilen.map((z) => (
        <Modellkarte
          key={z.id}
          zeile={z}
          api={api}
          projektId={projekt.id}
          profilId={profile[
            projekt.modelle.find((m) => m.id === z.id)?.revitVersion ?? 0] ?? null}
          offen={offen === z.id}
          umschalten={() => setOffen(offen === z.id ? null : z.id)}
          neuLaden={laden}
        />
      ))}
    </>
  );
}

// --- Eine Modellkarte -------------------------------------------------------

function Modellkarte({
  zeile: z, api, projektId, profilId, offen, umschalten, neuLaden,
}: {
  zeile: Planmodellzeile;
  api: ApiClient;
  projektId: string;
  /** Die Profilfassung. Ohne sie lässt sich kein Auftrag anlegen. */
  profilId: string | null;
  offen: boolean;
  umschalten: () => void;
  neuLaden: () => void;
}) {
  const [reiter, setReiter] = useState<'index' | 'plaene' | 'uploads'>('index');
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<number | null>(null);
  // TD-WR1: „In Modell übertragen" (41, Planrueckgabe) nimmt der Server seit S12 nur von ProjectAdmin und
  // SystemAdmin an - für alle anderen entfällt der Knopf. false, solange die Antwort fehlt oder scheitert.
  const darfSchreiben = useModellSchreibrecht(api);

  /**
   * Einen Auftrag anlegen.
   *
   * 40 liest Plandaten aus dem Modell, 41 überträgt sie zurück. Beide laufen über
   * die Agent-Maschinen und brauchen etwa eine halbe Stunde — das Modell wird mit
   * allen Verknüpfungen geladen.
   */
  const beauftragen = async (art: 40 | 41) => {
    if (!profilId) {
      setMeldung('Keine Profilfassung für dieses Modell gefunden. '
               + 'Ohne sie lässt sich kein Auftrag anlegen.');
      return;
    }

    setLaeuft(art);
    setMeldung(null);
    try {
      const a = await api.auftragAnlegen({
        projectId: projektId,
        exportProfileVersionId: profilId,
        modelIds: [z.id],
        kinds: [art],
      });

      setMeldung(`Auftrag ${a.jobNumber} angelegt. `
               + (art === 40
                 ? 'Die Plandaten erscheinen hier, sobald er durchgelaufen ist. '
                   + 'Pläne, die es im Modell nicht mehr gibt, werden dabei aus '
                   + 'dieser Liste entfernt.'
                 : 'Die Übertragung ins Modell läuft.')
               + ' Das dauert etwa eine halbe Stunde.');
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(null);
    }
  };

  const schluessel = async () => {
    try {
      // schicke() gibt T | void zurueck, weil manche Aufrufe keinen Rumpf haben.
      // Deshalb pruefen statt annehmen.
      const a = await api.schluesselVergeben(z.id);

      setMeldung(a && 'message' in a ? a.message : 'Schlüssel vergeben.');
      neuLaden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    }
  };

  const konflikte = z.plaeneKonflikt + z.revisionenKonflikt;

  return (
    <div className="karte">
      <div
        style={{ display: 'flex', alignItems: 'baseline', gap: 10,
                 flexWrap: 'wrap', cursor: 'pointer' }}
        onClick={umschalten}
      >
        <h3 style={{ margin: 0 }}>{z.name}</h3>

        {z.modellschluessel ? (
          /* Zwei Zustände, klar unterschieden: Der Schlüssel ist vergeben, und er
             steht im Modell. Vorher hieß es "nicht übertragen", obwohl das Add-in
             ihn geschrieben hatte — die Plattform erfuhr davon nichts. */
          <StatusBadge
            klein
            zustand={z.schluesselAm ? 'gut' : 'neutral'}
            text={`${z.modellschluessel}${
              z.schluesselAm ? ' (verbunden)' : ' (nicht übertragen)'}`}
            titel={z.schluesselAm
              ? `Im Modell bestätigt am `
                + `${new Date(z.schluesselAm).toLocaleDateString('de-DE')}`
              : 'Vergeben, aber noch nicht im Modell. Über das Add-in unter '
                + (darfSchreiben
                  ? '"Modellvorgaben" setzen oder mit "In Modell übertragen".'
                  : '"Modellvorgaben" setzen.')}
          />
        ) : (
          <button
            className="m-knopf"
            onClick={(e) => { e.stopPropagation(); schluessel(); }}
          >
            Schlüssel vergeben
          </button>
        )}

        {z.aufAcc && <StatusBadge zustand="neutral" text="ACC" klein />}

        {z.letzterIndex && (
          <StatusBadge
            klein zustand="neutral"
            text={`Index ${z.letzterIndex}${z.letztesDatum ? ` · ${z.letztesDatum}` : ''}`}
          />
        )}

        {konflikte > 0 && (
          <StatusBadge zustand="warnung" text={`${konflikte} Abweichungen`} klein />
        )}

        {z.offeneUebertragung > 0 && (
          <StatusBadge
            klein zustand="neutral"
            text={`${z.offeneUebertragung} zu übertragen`}
          />
        )}

        {z.entwuerfe > 0 && (
          <StatusBadge
            klein zustand="neutral"
            text={`${z.entwuerfe} Entwurf/Entwürfe`}
            titel="Entwürfe werden nicht ins Modell geschrieben"
          />
        )}

        <span className="schwach" style={{ marginLeft: 'auto', fontSize: 12 }}>
          {z.inListe} Pläne · {z.revisionen} Stände · {z.uploads} Uploads
          {' '}{offen ? '▴' : '▾'}
        </span>
      </div>

      {/* Die Verteilung nach Freigabestatus. P zur Prüfung, V Vorabzug,
          F Freigegeben. */}
      {z.jeStatus.length > 0 && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          {z.jeStatus.map((s) => (
            <span key={s.status} className="sehr-schwach" style={{ fontSize: 11 }}>
              {({ P: 'zur Prüfung', V: 'Vorabzug', F: 'freigegeben' } as
                Record<string, string>)[s.status] ?? s.status}: {s.anzahl}
            </span>
          ))}
        </div>
      )}

      {/* Holen und Zurückgeben.
      
          Ohne diese beiden Knöpfe zeigt die Seite nur, was zufällig schon im
          Bestand liegt — der Weg ins Modell und zurück fehlte. */}
      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap',
                    alignItems: 'center' }}>
        <button
          className="m-knopf"
          disabled={laeuft !== null || !profilId}
          onClick={(e) => { e.stopPropagation(); beauftragen(40); }}
          title="Pläne, Indexstände und Modellkennzahlen aus dem Modell lesen"
        >
          {laeuft === 40 ? 'wird beauftragt …' : 'Aus Modell lesen'}
        </button>

        {darfSchreiben && (
          <button
            className={z.offeneUebertragung > 0 ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
            disabled={laeuft !== null || !profilId
                      || (z.offeneUebertragung === 0 && !z.modellschluessel)}
            onClick={(e) => { e.stopPropagation(); beauftragen(41); }}
            title={z.offeneUebertragung > 0
              ? `${z.offeneUebertragung} Stand/Stände übertragen`
              : z.entwuerfe > 0
                ? `${z.entwuerfe} Stand/Stände sind noch Entwurf — erst fixieren`
                : 'Nichts zu übertragen'}
          >
            {laeuft === 41 ? 'wird beauftragt …' : 'In Modell übertragen'}
            {z.offeneUebertragung > 0 && ` (${z.offeneUebertragung})`}
          </button>
        )}

        {!profilId && (
          <span className="sehr-schwach" style={{ fontSize: 11 }}>
            keine Profilfassung — Aufträge nicht möglich
          </span>
        )}

        {(z.plaeneKonflikt + z.revisionenKonflikt) > 0 && (
          <span className="sehr-schwach" style={{ fontSize: 11,
                                                  color: 'var(--warnung)' }}>
            Abweichungen erst freigeben, sonst werden sie beim Übertragen
            ausgelassen
          </span>
        )}
      </div>

      {meldung && (
        <div className="hinweis" style={{ marginTop: 10 }}>
          <IconOk size={13} /> {meldung}
        </div>
      )}

      {offen && (
        <>
          <div className="reiter" role="tablist" style={{ marginTop: 14 }}>
            {(['index', 'plaene', 'uploads'] as const).map((r) => (
              <button
                key={r}
                type="button"
                role="tab"
                aria-selected={reiter === r}
                className={reiter === r ? 'aktiv' : undefined}
                onClick={() => setReiter(r)}
              >
                {{ index: 'Indexstände', plaene: 'Pläne',
                   uploads: 'Modelluploads' }[r]}
              </button>
            ))}
          </div>

          {reiter === 'index' && (
            <Indexstaende modelId={z.id} api={api} neuLaden={neuLaden} />
          )}
          {reiter === 'plaene' && (
            <Plaene modelId={z.id} api={api} neuLaden={neuLaden} />
          )}
          {reiter === 'uploads' && (
            <Uploads modelId={z.id} api={api} neuLaden={neuLaden} />
          )}
        </>
      )}
    </div>
  );
}

// --- Indexstände ------------------------------------------------------------

function Indexstaende({
  modelId, api, neuLaden,
}: { modelId: string; api: ApiClient; neuLaden: () => void }) {
  const [liste, setListe] = useState<Planrevision[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [neu, setNeu] = useState(false);

  const laden = async () => {
    try { setListe(await api.modellrevisionen(modelId)); setFehler(null); }
    catch (e) { setFehler(e); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { laden(); }, [api, modelId]);

  const spalten: Spalte<Planrevision>[] = [
    {
      schluessel: 'folge', titel: 'Folge', art: 'zahl', breite: '80px',
      wert: (r) => r.folge,
    },
    {
      schluessel: 'index', titel: 'Index', breite: '90px', immerSichtbar: true,
      wert: (r) => r.index ?? '',
      inhalt: (r) => <span className="zuordnung-name">{r.index ?? '—'}</span>,
    },
    {
      schluessel: 'datum', titel: 'Datum', breite: '140px', sortierbar: false,
      wert: (r) => r.datum ?? '',
      inhalt: (r) => (
        <Indexfeld
          r={r} api={api} feld="datum" platzhalter="TT.MM.JJJJ"
          neuLaden={() => { laden(); neuLaden(); }}
        />
      ),
    },
    {
      /* Auch die Beschreibung änderbar — solange nicht fixiert. Bei einem
         Entwurf ändert sich der Text so oft wie das Datum. */
      schluessel: 'beschreibung', titel: 'Beschreibung', sortierbar: false,
      wert: (r) => r.beschreibung ?? '',
      inhalt: (r) => (
        <Indexfeld
          r={r} api={api} feld="beschreibung" platzhalter="Planstand …"
          neuLaden={() => { laden(); neuLaden(); }}
        />
      ),
    },
    {
      schluessel: 'plaene', titel: 'Pläne', art: 'zahl', breite: '90px',
      wert: (r) => r.plaeneAnzahl,
    },
    {
      schluessel: 'zustand', titel: 'Zustand', art: 'status', breite: '230px',
      wert: (r) => (r.istFixiert ? 'fixiert' : 'Entwurf'),
      inhalt: (r) => (
        <>
          {r.istFixiert
            ? (
              <StatusBadge
                zustand="gut" text="fixiert" klein
                titel={r.fixiertAm
                  ? `fixiert am ${new Date(r.fixiertAm).toLocaleDateString('de-DE')}`
                    + (r.fixiertVon ? ` von ${r.fixiertVon}` : '')
                  : 'fixiert'}
              />
            )
            : (
              <StatusBadge
                zustand="neutral" text="Entwurf" klein
                titel="Entwurf — wird nicht ins Modell geschrieben"
              />
            )}
          {r.ausgegeben && (
            <> <StatusBadge zustand="neutral" text="ausgegeben" klein
                            titel="In Revit gesperrt" /></>
          )}
          {r.herkunft === 'app' && (
            <> <StatusBadge zustand="neutral" text="App" klein /></>
          )}
          {r.istFixiert && r.uebertragenAm === null && (
            <> <StatusBadge zustand="warnung" text="nicht übertragen" klein /></>
          )}
          {r.hatKonflikt && r.konfliktHinweis && (
            <div className="sehr-schwach" style={{ fontSize: 11 }}>
              {r.konfliktHinweis}
            </div>
          )}
        </>
      ),
    },
    {
      schluessel: 'aktionen', titel: '', sortierbar: false, ausrichtung: 'rechts',
      breite: '220px',
      inhalt: (r) => (
        <Indexaktionen r={r} api={api} neuLaden={() => { laden(); neuLaden(); }} />
      ),
    },
  ];

  return (
    <div style={{ marginTop: 12 }}>
      <p className="schwach" style={{ marginTop: 0, fontSize: 12 }}>
        Der Index entsteht in Revit aus der Nummerierungsfolge — die Plattform bestimmt
        die Reihenfolge, nicht den Text. Ein neuer Stand gilt dem ganzen Plansatz.
      </p>

      {/* Der Entwurfszustand ist der eigentliche Gewinn: In der Bearbeitung ändert
          sich noch oft etwas, und ein Stand, der schon im Modell steht, lässt sich
          nur über den Umweg über die Sperre berichtigen. */}
      <p className="sehr-schwach" style={{ marginTop: 0, fontSize: 11 }}>
        Ein neuer Stand ist zunächst <strong>Entwurf</strong> — änderbar, löschbar,
        und er wird nicht ins Modell geschrieben. Erst <strong>fixieren</strong> macht
        ihn verbindlich. Was aus dem Modell gelesen wurde, kommt fixiert an; zum
        Ändern erst lösen.
      </p>

      <DataTable
        name="Indexstände"
        spalten={spalten}
        zeilen={liste ?? []}
        schluessel={(r) => r.id}
        laedt={liste === null && fehler === null}
        fehler={liste === null ? fehler : undefined}
        beiErneutVersuchen={laden}
        sortierung={{ spalte: 'folge', richtung: 'auf' }}
        zeilenKlasse={(r) => (r.hatKonflikt ? 'datenfeld' : undefined)}
        suche={(liste ?? []).length > 8}
        dicht
        leer={(
          <EmptyState
            titel="Noch kein Indexstand."
            erklaerung={'Ein Stand gilt dem ganzen Plansatz und entsteht entweder beim '
              + 'Auslesen des Modells oder hier von Hand.'}
            aktion={(
              <button className="m-knopf m-knopf-haupt" onClick={() => setNeu(true)}>
                Neuen Indexstand anlegen
              </button>
            )}
          />
        )}
      />

      {fehler !== null && liste !== null && (
        <ErrorState klein meldung="Die letzte Änderung ist fehlgeschlagen."
                    technisch={fehler} />
      )}

      {neu ? (
        <NeuerStand
          modelId={modelId}
          api={api}
          fertig={() => { setNeu(false); laden(); neuLaden(); }}
          abbrechen={() => setNeu(false)}
        />
      ) : (
        <button className="m-knopf" onClick={() => setNeu(true)}
                style={{ marginTop: 10 }}>
          Neuen Indexstand anlegen
        </button>
      )}
    </div>
  );
}

/**
 * Ein änderbares Feld eines Indexstands.
 *
 * Geschrieben wird beim VERLASSEN und nicht bei jedem Tastendruck — sonst
 * stünde ein halb getipptes Datum als gültiger Wert in der Datenbank. Fixiert
 * heißt verbindlich; dann ist nichts änderbar.
 */
function Indexfeld({
  r, api, feld, platzhalter, neuLaden,
}: {
  r: Planrevision;
  api: ApiClient;
  feld: 'datum' | 'beschreibung';
  platzhalter: string;
  neuLaden: () => void;
}) {
  const alterWert = (feld === 'datum' ? r.datum : r.beschreibung) ?? '';
  const [wert, setWert] = useState(alterWert);
  const [laeuft, setLaeuft] = useState(false);

  const speichern = async () => {
    if (wert === alterWert) return;

    setLaeuft(true);
    try {
      await api.revisionAendern(r.id, feld === 'datum'
        ? { datum: wert }
        : { beschreibung: wert });
      neuLaden();
    } catch {
      setWert(alterWert);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <>
      <label className="m-nur-vorlesen" htmlFor={`rev-${r.id}-${feld}`}>
        {feld === 'datum' ? 'Datum' : 'Beschreibung'} des Stands {r.folge}
      </label>
      <input
        id={`rev-${r.id}-${feld}`}
        value={wert}
        onChange={(e) => setWert(e.target.value)}
        onBlur={speichern}
        onKeyDown={(e) => { if (e.key === 'Enter') speichern(); }}
        onClick={(e) => e.stopPropagation()}
        disabled={laeuft || r.istFixiert}
        placeholder={platzhalter}
        style={{ width: '100%', fontSize: 12 }}
      />
    </>
  );
}

/** Fixieren, lösen, entfernen, freigeben — die Knöpfe eines Indexstands. */
function Indexaktionen({
  r, api, neuLaden,
}: { r: Planrevision; api: ApiClient; neuLaden: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [frage, setFrage] = useState(false);

  const tun = async (was: () => Promise<unknown>) => {
    setLaeuft(true);
    try { await was(); neuLaden(); }
    finally { setLaeuft(false); }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {r.istFixiert ? (
        <button
          className="m-knopf"
          disabled={laeuft}
          onClick={(e) => { e.stopPropagation(); tun(() => api.revisionLoesen(r.id)); }}
          title="Wieder änderbar machen"
        >
          lösen
        </button>
      ) : (
        <>
          <button
            className="m-knopf m-knopf-haupt"
            disabled={laeuft || !r.datum}
            onClick={(e) => { e.stopPropagation(); tun(() => api.revisionFixieren(r.id)); }}
            title="Verbindlich machen — erst dann wird er ins Modell geschrieben"
          >
            fixieren
          </button>

          <button
            className="m-knopf m-knopf-nur-zeichen"
            disabled={laeuft}
            aria-label={`Stand ${r.folge} entfernen`}
            onClick={(e) => { e.stopPropagation(); setFrage(true); }}
            title="Aus der Plattform entfernen"
          >
            ×
          </button>
        </>
      )}

      {r.hatKonflikt && (
        <button
          className="m-knopf"
          disabled={laeuft}
          onClick={(e) => { e.stopPropagation(); tun(() => api.revisionFreigeben(r.id)); }}
          title="Der Stand der Plattform gilt"
        >
          freigeben
        </button>
      )}

      <ConfirmDialog
        offen={frage}
        art="loeschen"
        titel={`Stand ${r.folge} entfernen?`}
        text={r.index || r.uebertragenAm
          ? 'Er steht im Modell und kommt beim nächsten Auslesen wieder, solange er '
            + 'nicht in Revit gelöscht wird.'
          : undefined}
        laeuft={laeuft}
        beiBestaetigen={async () => {
          setFrage(false);
          await tun(() => api.revisionEntfernen(r.id));
        }}
        beiAbbrechen={() => setFrage(false)}
      />
    </span>
  );
}

function NeuerStand({
  modelId, api, fertig, abbrechen,
}: {
  modelId: string; api: ApiClient; fertig: () => void; abbrechen: () => void;
}) {
  const heute = new Date().toLocaleDateString('de-DE');
  const [datum, setDatum] = useState(heute);
  const [beschreibung, setBeschreibung] = useState('');
  const [ausgegeben, setAusgegeben] = useState(true);
  const [fehler, setFehler] = useState<unknown>(null);
  const [laeuft, setLaeuft] = useState(false);

  const anlegen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.revisionAnlegen(modelId, { datum, beschreibung, ausgegeben });
      fertig();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel="Neuer Indexstand"
      beschreibung={'Ausgegeben bedeutet in Revit gesperrt. Der Stand entsteht als '
        + 'ENTWURF und wird noch nicht übertragen — erst nach dem Fixieren wird er '
        + 'beim nächsten Übertragen angelegt und dem ganzen Plansatz zugeordnet.'}
      spalten={2}
      fuss={(
        <>
          <button className="m-knopf" disabled={laeuft} onClick={abbrechen}>
            Abbrechen
          </button>
          <button className="m-knopf m-knopf-haupt" disabled={laeuft} onClick={anlegen}>
            Anlegen
          </button>
        </>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Der Indexstand konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        beschriftung="Datum"
        wert={datum}
        beiAenderung={(w) => setDatum(w as string)}
        platzhalter="TT.MM.JJJJ"
        pflicht
      />
      <Feld
        beschriftung="Beschreibung"
        wert={beschreibung}
        beiAenderung={(w) => setBeschreibung(w as string)}
        platzhalter="Planexport LPH3"
      />
      <Feld
        art="haken"
        beschriftung="ausgegeben"
        wert={ausgegeben}
        beiAenderung={(w) => setAusgegeben(w as boolean)}
      />
    </FormSection>
  );
}

// --- Pläne ------------------------------------------------------------------

const StatusText: Record<string, string> = {
  P: 'zur Prüfung', V: 'Vorabzug', F: 'freigegeben',
};

function Plaene({
  modelId, api, neuLaden,
}: { modelId: string; api: ApiClient; neuLaden: () => void }) {
  const [liste, setListe] = useState<Planblatt[] | null>(null);
  const [nurListe, setNurListe] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);
  const [meldung, setMeldung] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  const laden = async () => {
    try { setListe(await api.modellplaene(modelId, nurListe)); setFehler(null); }
    catch (e) { setFehler(e); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { laden(); }, [api, modelId, nurListe]);

  const tun = async (was: () => Promise<unknown>) => {
    setLaeuft(true);
    setMeldung(null);
    try {
      const a = await was();
      setMeldung(a && typeof a === 'object' && 'message' in a
        ? String((a as { message: unknown }).message) : null);
      await laden();
      neuLaden();
    } catch (e) {
      setMeldung(e instanceof Error ? e.message : String(e));
    } finally {
      setLaeuft(false);
    }
  };

  if (fehler && !liste) {
    return (
      <ErrorState
        meldung="Die Pläne konnten nicht geladen werden."
        technisch={fehler}
        beiErneutVersuchen={laden}
      />
    );
  }

  if (!liste) return <LoadingState text="Pläne werden geladen …" zeilen={8} spalten={5} />;

  const titelbloecke = [...new Set(liste.map((b) => b.titelblock).filter(Boolean))];
  const inPlanliste = liste.filter((b) => b.inPlanliste).length;

  return (
    <div style={{ marginTop: 12 }}>
      {/* Die Auswahl für die Planliste steht oben: Sie ist der Zweck dieses
          Reiters, nicht eine Nebensache am Ende. */}
      <div className="modellfeld" style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center',
                      flexWrap: 'wrap' }}>
          <div>
            <div className="feldname">Planliste</div>
            <div style={{ fontSize: 14 }}>
              {inPlanliste}
              <span className="sehr-schwach" style={{ fontSize: 12 }}>
                {' '}von {liste.length} Plänen
              </span>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap',
                        marginLeft: 'auto' }}>
            <button
              className="m-knopf"
              disabled={laeuft}
              onClick={() => tun(() => api.planlisteAuswahl(modelId, true))}
              title="Jeden Plan des Modells aufnehmen"
            >
              Alle übernehmen
            </button>

            <button
              className="m-knopf"
              disabled={laeuft}
              onClick={() => tun(() => api.planlisteAuswahl(modelId, true, true))}
              title="Nur die, die Revit in seiner Planliste führt — der häufigste
                     Ausgangspunkt"
            >
              Nur Revit-Liste
            </button>

            <button
              className="m-knopf"
              disabled={laeuft}
              onClick={() => tun(() => api.planlisteAuswahl(modelId, false))}
            >
              Alle entfernen
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap',
                      alignItems: 'center' }}>
          <button
            className="m-knopf m-knopf-haupt"
            disabled={laeuft || inPlanliste === 0}
            onClick={() => api.planlisteLaden(modelId, 'pdf')}
            title={inPlanliste === 0
              ? 'Kein Plan gewählt'
              : `${inPlanliste} Pläne als PDF`}
          >
            Planliste als PDF
          </button>

          <button
            className="m-knopf"
            disabled={laeuft || inPlanliste === 0}
            onClick={() => api.planlisteLaden(modelId, 'xlsx')}
            title={inPlanliste === 0
              ? 'Kein Plan gewählt'
              : `${inPlanliste} Pläne als Tabelle`}
          >
            als Excel
          </button>

          {/* A1 als Vorgabe, wie das Add-in es holt. Revit rastert das PDF, und
              eine doppelt so große Quellseite ergibt beim Halbieren im Modell die
              doppelte Punktdichte. */}
          <button
            className="m-knopf"
            disabled={laeuft}
            onClick={() => api.dashboardLaden(modelId, 'a1')}
            title="Die Übersicht für die Startansicht — A1, im Modell auf die halbe
                   Größe zu setzen. Auf den Plan bringt sie das Add-in unter
                   Modellvorgaben, Schritt 7."
          >
            Dashboard ansehen
          </button>

          <button
            className="m-knopf"
            disabled={laeuft}
            onClick={() => api.dashboardLaden(modelId, 'a0')}
            title="Noch größer, falls A1 im Modell zu grob erscheint — auf ein Drittel
                   zu verkleinern"
            style={{ padding: '3px 8px', fontSize: 11 }}
          >
            A0
          </button>

          <span className="sehr-schwach" style={{ fontSize: 11 }}>
            Plannummer, Index, Ebene, Gewerk, Bereich, Datum
          </span>
        </div>

        {meldung && (
          <div className="sehr-schwach" style={{ fontSize: 11, marginTop: 8 }}>
            {meldung}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 14, alignItems: 'center',
                    flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13 }}>
          <input type="checkbox" checked={nurListe}
                 onChange={(e) => setNurListe(e.target.checked)} />
          Nur Pläne aus der Revit-Planliste
        </label>

        <span className="sehr-schwach" style={{ fontSize: 11 }}>
          Beim Lesen aus dem Modell wird abgeglichen: Neue kommen hinzu, nicht
          mehr vorhandene werden entfernt. Ihre Auswahl bleibt dabei erhalten.
        </span>
      </div>

      {titelbloecke.length > 1 && (
        <div className="hinweis warnung" style={{ marginBottom: 10 }}>
          <span className="hinweis-zeichen">!</span>
          <div>
            <strong>{titelbloecke.length} Titelblockfamilien</strong> in diesem Modell.
            <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
              {titelbloecke.map((t) => (
                <li key={t} style={{ fontSize: 11 }}>{t}</li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <DataTable
        name="Pläne"
        spalten={planspalten(api, laeuft, tun)}
        zeilen={liste}
        schluessel={(b) => b.id}
        hoehe={460}
        ausfuhr
        ausfuhrDateiname="Plaene"
        zeilenKlasse={(b) => (b.hatKonflikt ? 'datenfeld' : undefined)}
        suchePlatzhalter="Plannummer, Name, Ebene …"
        dicht
        leer={(
          <EmptyState
            titel="Keine Pläne."
            erklaerung={'Sie entstehen beim Leselauf über den Agenten oder das Add-in — '
              + 'oben „Pläne lesen".'}
          />
        )}
      />
    </div>
  );
}

/**
 * Die Spalten der Planliste.
 *
 * Sie stehen als Funktion und nicht als Konstante, weil zwei Zellen handeln:
 * der Haken „in die Planliste" und der Knopf „freigeben". Beide brauchen
 * `api` und den laufenden Zustand.
 */
function planspalten(
  api: ApiClient,
  laeuft: boolean,
  tun: (was: () => Promise<unknown>) => Promise<void>,
): Spalte<Planblatt>[] {
  return [
    {
      schluessel: 'liste', titel: 'Liste', breite: '70px', sortierbar: false,
      ausrichtung: 'mitte',
      titelHinweis: 'In die Planliste aufnehmen',
      wert: (b) => b.inPlanliste,
      inhalt: (b) => (
        <>
          {/* Je Plan ein Haken. Er wirkt sofort — ein Speichern-Knopf für eine
              Auswahl wäre ein Handgriff zu viel. */}
          <label className="m-nur-vorlesen" htmlFor={`plan-${b.id}-liste`}>
            {b.plannummer} in die Planliste aufnehmen
          </label>
          <input
            id={`plan-${b.id}-liste`}
            type="checkbox"
            checked={b.inPlanliste}
            disabled={laeuft}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => tun(() =>
              api.planAendern(b.id, { inPlanliste: e.target.checked }))}
            title={b.inPlanliste ? 'In der Planliste' : 'Nicht in der Planliste'}
          />
        </>
      ),
    },
    {
      schluessel: 'plannummer', titel: 'Plannummer', immerSichtbar: true,
      breite: '190px',
      wert: (b) => b.plannummer,
      inhalt: (b) => (
        <span className="zuordnung-name" style={{ fontSize: 12 }}>
          {b.plannummer}
          {b.gattung && (
            <div className="sehr-schwach" style={{ fontSize: 10 }}>
              {b.gattung} · {b.gewerk} · {b.laufnummer}
            </div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'index', titel: 'Index', breite: '100px',
      wert: (b) => b.aktuellerIndex ?? '',
      inhalt: (b) => (
        <span className="zuordnung-name" style={{ fontSize: 12 }}>
          {b.aktuellerIndex ?? '—'}
          {/* Kennzeichnen, woher der Wert kommt. Ein Index aus dem Plansatz gilt
              für alle Blätter; einer am Blatt wurde dort einzeln zugeordnet und
              ist genauer. */}
          {b.indexAusSatz && (
            <span className="sehr-schwach" style={{ fontSize: 9 }}
                  title="Aus dem Plansatz — dem Blatt ist kein eigener Stand zugeordnet">
              {' '}Satz
            </span>
          )}
        </span>
      ),
    },
    {
      schluessel: 'name', titel: 'Name',
      wert: (b) => b.planname ?? '',
      inhalt: (b) => (
        <span className="schwach" style={{ fontSize: 12 }}>{b.planname ?? '—'}</span>
      ),
    },
    {
      schluessel: 'ebene', titel: 'Ebene', breite: '120px',
      wert: (b) => b.geschoss ?? b.bauwerksteil ?? '',
      inhalt: (b) => (
        <span className="schwach">{b.geschoss ?? b.bauwerksteil ?? '—'}</span>
      ),
    },
    {
      schluessel: 'massstab', titel: 'Maßstab', breite: '110px',
      wert: (b) => b.massstab ?? '',
      inhalt: (b) => <span className="schwach">{b.massstab ?? '—'}</span>,
    },
    {
      schluessel: 'datum', titel: 'Datum', art: 'datum', breite: '120px',
      wert: (b) => b.plandatum ?? '',
      inhalt: (b) => <span className="schwach">{b.plandatum ?? '—'}</span>,
    },
    {
      schluessel: 'status', titel: 'Status', art: 'status', breite: '200px',
      wert: (b) => StatusText[b.freigabestatus ?? ''] ?? b.freigabestatus ?? '',
      inhalt: (b) => (
        <>
          {b.freigabestatus && (
            <StatusBadge
              klein
              zustand={b.freigabestatus === 'F' ? 'gut' : 'neutral'}
              text={StatusText[b.freigabestatus] ?? b.freigabestatus}
            />
          )}
          {!b.inListe && (
            <> <StatusBadge
              zustand="neutral" text="nicht in Revit-Liste" klein
              titel="Revit führt diesen Plan nicht in seiner Planliste"
            /></>
          )}
        </>
      ),
    },
    {
      schluessel: 'konflikt', titel: 'Konflikt', sortierbar: false, breite: '200px',
      wert: (b) => (b.hatKonflikt ? b.konfliktHinweis ?? 'Konflikt' : ''),
      inhalt: (b) => (b.hatKonflikt ? (
        <div>
          <div className="sehr-schwach" style={{ fontSize: 10 }}>
            {b.konfliktHinweis}
          </div>
          <button
            className="m-knopf"
            onClick={(e) => { e.stopPropagation(); tun(() => api.planFreigeben(b.id)); }}
          >
            freigeben
          </button>
        </div>
      ) : null),
    },
  ];
}

// --- Modelluploads ----------------------------------------------------------

const ZielText: Record<string, string> = {
  acc: 'ACC', conclude: 'Conclude', sonstige: 'sonstige',
};

function Uploads({
  modelId, api, neuLaden,
}: { modelId: string; api: ApiClient; neuLaden: () => void }) {
  const [liste, setListe] = useState<Modellupload[] | null>(null);
  const [fehler, setFehler] = useState<unknown>(null);
  const [neu, setNeu] = useState(false);

  const laden = async () => {
    try { setListe(await api.modelluploads(modelId)); setFehler(null); }
    catch (e) { setFehler(e); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { laden(); }, [api, modelId]);

  const nachLaden = () => { laden(); neuLaden(); };

  const spalten: Spalte<Modellupload>[] = [
    {
      schluessel: 'folge', titel: 'Nr.', art: 'zahl', breite: '70px',
      wert: (u) => u.folge,
    },
    {
      schluessel: 'datum', titel: 'Datum', art: 'datum', breite: '130px',
      immerSichtbar: true,
      wert: (u) => u.datum,
      inhalt: (u) => new Date(u.datum).toLocaleDateString('de-DE'),
    },
    {
      schluessel: 'phase', titel: 'Phase', breite: '120px', sortierbar: false,
      wert: (u) => u.leistungsphase ?? '',
      inhalt: (u) => <Uploadphase u={u} api={api} neuLaden={nachLaden} />,
    },
    {
      schluessel: 'ziel', titel: 'Ziel', breite: '150px',
      wert: (u) => (u.ziel ? ZielText[u.ziel] ?? u.ziel : ''),
      inhalt: (u) => (
        <span className="schwach">
          {u.ziel ? (ZielText[u.ziel] ?? u.ziel) : '—'}
          {u.zielHinweis && (
            <div className="sehr-schwach" style={{ fontSize: 10 }}>{u.zielHinweis}</div>
          )}
        </span>
      ),
    },
    {
      schluessel: 'bemerkung', titel: 'Bemerkung', sortierbar: false,
      wert: (u) => u.bemerkung ?? '',
      inhalt: (u) => <Uploadbemerkung u={u} api={api} neuLaden={nachLaden} />,
    },
    {
      schluessel: 'ersteller', titel: 'Ersteller', breite: '150px',
      wert: (u) => u.ersteller ?? '',
      inhalt: (u) => (
        <span className="sehr-schwach" style={{ fontSize: 11 }}>
          {u.ersteller ?? '—'}
        </span>
      ),
    },
    {
      schluessel: 'aktionen', titel: '', sortierbar: false, ausrichtung: 'rechts',
      breite: '220px',
      inhalt: (u) => <Uploadaktionen u={u} api={api} neuLaden={nachLaden} />,
    },
  ];

  return (
    <div style={{ marginTop: 12 }}>
      <p className="schwach" style={{ marginTop: 0, fontSize: 12 }}>
        Die Dokumentation der Modellübergaben — getrennt von den Planrevisionen. Bei
        drei Jahren Laufzeit werden das hundert und mehr Einträge; deshalb liegt sie
        hier und nicht in einer Revit-Bauteilliste.
      </p>

      {neu && (
        <NeuerUpload
          modelId={modelId}
          api={api}
          fertig={() => { setNeu(false); nachLaden(); }}
          abbrechen={() => setNeu(false)}
        />
      )}

      <DataTable
        name="Modelluploads"
        spalten={spalten}
        zeilen={liste ?? []}
        schluessel={(u) => u.id}
        laedt={liste === null && fehler === null}
        fehler={liste === null ? fehler : undefined}
        beiErneutVersuchen={laden}
        sortierung={{ spalte: 'folge', richtung: 'ab' }}
        hoehe={420}
        ausfuhr
        ausfuhrDateiname="Modelluploads"
        dicht
        leer={(
          <EmptyState
            icon={<IconHochladen />}
            titel="Noch kein Upload vermerkt."
            erklaerung={'Ein Eintrag hält fest, wann welcher Modellstand wohin '
              + 'übergeben wurde — bei drei Jahren Laufzeit die einzige Chronik, die '
              + 'es davon gibt.'}
            aktion={(
              <button className="m-knopf m-knopf-haupt" onClick={() => setNeu(true)}>
                <IconHochladen size={15} />Upload vermerken
              </button>
            )}
          />
        )}
      />

      {fehler !== null && liste !== null && (
        <ErrorState klein meldung="Die letzte Änderung ist fehlgeschlagen."
                    technisch={fehler} />
      )}

      {!neu && (liste ?? []).length > 0 && (
        <button className="m-knopf" onClick={() => setNeu(true)} style={{ marginTop: 10 }}>
          <IconHochladen size={15} />Upload vermerken
        </button>
      )}
    </div>
  );
}

/**
 * Die Leistungsphase eines Uploads.
 *
 * Dieselbe Regel wie beim Planindex: Ein Entwurf ist änderbar, ein fixierter
 * ist Dokumentation und wird nicht mehr angefasst.
 */
function Uploadphase({
  u, api, neuLaden,
}: { u: Modellupload; api: ApiClient; neuLaden: () => void }) {
  const [phase, setPhase] = useState(u.leistungsphase ?? '');
  const [laeuft, setLaeuft] = useState(false);

  if (u.istFixiert) {
    return <span className="schwach">{u.leistungsphase ?? '—'}</span>;
  }

  return (
    <>
      <label className="m-nur-vorlesen" htmlFor={`upload-${u.id}-phase`}>
        Leistungsphase des Eintrags {u.folge}
      </label>
      <select
        id={`upload-${u.id}-phase`}
        value={phase}
        disabled={laeuft}
        onClick={(e) => e.stopPropagation()}
        onChange={async (e) => {
          const wert = e.target.value;
          setPhase(wert);
          setLaeuft(true);
          try { await api.uploadAendern(u.id, { leistungsphase: wert }); neuLaden(); }
          catch { setPhase(u.leistungsphase ?? ''); }
          finally { setLaeuft(false); }
        }}
        style={{ fontSize: 12 }}
      >
        <option value="">—</option>
        {['LPH2', 'LPH3', 'LPH4', 'LPH5', 'LPH6', 'LPH7'].map((p2) => (
          <option key={p2} value={p2}>{p2}</option>
        ))}
      </select>
    </>
  );
}

/** Die Bemerkung eines Uploads — geschrieben beim Verlassen des Feldes. */
function Uploadbemerkung({
  u, api, neuLaden,
}: { u: Modellupload; api: ApiClient; neuLaden: () => void }) {
  const [bemerkung, setBemerkung] = useState(u.bemerkung ?? '');
  const [laeuft, setLaeuft] = useState(false);

  if (u.istFixiert) {
    return (
      <span className="schwach" style={{ fontSize: 12 }}>{u.bemerkung ?? '—'}</span>
    );
  }

  return (
    <>
      <label className="m-nur-vorlesen" htmlFor={`upload-${u.id}-bemerkung`}>
        Bemerkung zum Eintrag {u.folge}
      </label>
      <input
        id={`upload-${u.id}-bemerkung`}
        value={bemerkung}
        disabled={laeuft}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setBemerkung(e.target.value)}
        onBlur={async () => {
          if (bemerkung === (u.bemerkung ?? '')) return;
          setLaeuft(true);
          try { await api.uploadAendern(u.id, { bemerkung }); neuLaden(); }
          catch { setBemerkung(u.bemerkung ?? ''); }
          finally { setLaeuft(false); }
        }}
        style={{ width: '100%', fontSize: 12 }}
      />
    </>
  );
}

/** Fixieren, lösen, entfernen — die Knöpfe eines Uploadeintrags. */
function Uploadaktionen({
  u, api, neuLaden,
}: { u: Modellupload; api: ApiClient; neuLaden: () => void }) {
  const [laeuft, setLaeuft] = useState(false);
  const [frage, setFrage] = useState(false);

  const tun = async (was: () => Promise<unknown>) => {
    setLaeuft(true);
    try { await was(); neuLaden(); }
    finally { setLaeuft(false); }
  };

  return (
    <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
      {u.istFixiert ? (
        <>
          <StatusBadge
            zustand="gut" text="fixiert" klein
            titel={u.fixiertAm
              ? `fixiert am ${new Date(u.fixiertAm).toLocaleDateString('de-DE')}`
              : 'fixiert'}
          />
          <button className="m-knopf" disabled={laeuft}
                  onClick={(e) => { e.stopPropagation(); tun(() => api.uploadLoesen(u.id)); }}>
            lösen
          </button>
        </>
      ) : (
        <>
          <button
            className="m-knopf m-knopf-haupt"
            disabled={laeuft}
            title="Als Dokumentation festschreiben"
            onClick={(e) => { e.stopPropagation(); tun(() => api.uploadFixieren(u.id)); }}
          >
            fixieren
          </button>
          <button
            className="m-knopf m-knopf-nur-zeichen"
            disabled={laeuft}
            aria-label={`Eintrag ${u.folge} entfernen`}
            onClick={(e) => { e.stopPropagation(); setFrage(true); }}
          >
            ×
          </button>
        </>
      )}

      <ConfirmDialog
        offen={frage}
        art="loeschen"
        titel={`Eintrag ${u.folge} entfernen?`}
        text="Die Chronik der Modellübergaben verliert diesen Eintrag."
        laeuft={laeuft}
        beiBestaetigen={async () => {
          setFrage(false);
          await tun(() => api.uploadEntfernen(u.id));
        }}
        beiAbbrechen={() => setFrage(false)}
      />
    </span>
  );
}

function NeuerUpload({
  modelId, api, fertig, abbrechen,
}: {
  modelId: string; api: ApiClient; fertig: () => void; abbrechen: () => void;
}) {
  const [datum, setDatum] = useState(new Date().toISOString().slice(0, 10));
  const [phase, setPhase] = useState('LPH3');
  const [ziel, setZiel] = useState('acc');
  const [zielHinweis, setZielHinweis] = useState('');
  const [bemerkung, setBemerkung] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<unknown>(null);

  const anlegen = async () => {
    setLaeuft(true);
    setFehler(null);
    try {
      await api.uploadAnlegen(modelId, {
        datum: new Date(datum).toISOString(),
        leistungsphase: phase,
        ziel,
        zielHinweis: zielHinweis || undefined,
        bemerkung: bemerkung || undefined,
      });
      fertig();
    } catch (e) {
      setFehler(e);
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <FormSection
      titel="Upload vermerken"
      spalten={2}
      fuss={(
        <>
          <button className="m-knopf" disabled={laeuft} onClick={abbrechen}>
            Abbrechen
          </button>
          <button className="m-knopf m-knopf-haupt" disabled={laeuft} onClick={anlegen}>
            Vermerken
          </button>
        </>
      )}
    >
      {fehler !== null && (
        <ErrorState klein meldung="Der Eintrag konnte nicht angelegt werden."
                    technisch={fehler} />
      )}

      <Feld
        art="datum"
        beschriftung="Datum"
        wert={datum}
        beiAenderung={(w) => setDatum(w as string)}
        pflicht
      />
      <Feld
        art="auswahl"
        beschriftung="Leistungsphase"
        wert={phase}
        beiAenderung={(w) => setPhase(w as string)}
        optionen={['LPH2', 'LPH3', 'LPH4', 'LPH5', 'LPH6', 'LPH7']
          .map((p2) => ({ wert: p2, beschriftung: p2 }))}
      />
      <Feld
        art="auswahl"
        beschriftung="Ziel"
        wert={ziel}
        beiAenderung={(w) => setZiel(w as string)}
        optionen={[
          { wert: 'acc', beschriftung: 'ACC' },
          { wert: 'conclude', beschriftung: 'Conclude' },
          { wert: 'sonstige', beschriftung: 'sonstige' },
        ]}
      />
      {ziel === 'sonstige' && (
        <Feld
          beschriftung="Welche Plattform"
          wert={zielHinweis}
          beiAenderung={(w) => setZielHinweis(w as string)}
          pflicht
        />
      )}
      <Feld
        art="textfeld"
        beschriftung="Bemerkung"
        wert={bemerkung}
        beiAenderung={(w) => setBemerkung(w as string)}
        zeilen={2}
        platzhalter="Was in diesem Stand geändert wurde"
        breite="voll"
      />
    </FormSection>
  );
}
